import { BooleanInput } from '@angular/cdk/coercion';
import { NgClass, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter } from 'rxjs/operators';
import { DevstroupeConfigService } from '@devstroupe/services/config';
import { UserModel } from 'app/models/user.model';
import { UserFormComponent } from 'app/modules/configurations/access/user/form/user-form.component';
import { ApiService } from 'app/shared/apis/api.service';
import { SettingsService } from 'app/shared/settings/settings.service';
import { Subject, take, takeUntil } from 'rxjs';
import { AutoTranslateDirective } from 'app/shared/directives/auto-translate.directive';
import { AutoTranslatePipe } from 'app/shared/pipes/translate.pipe';
import { REQUESTER_HOME_PATH, isRequesterPortalContext } from 'app/core/auth/requester-user';
import { CompanyService } from 'app/services/company.service';
import { ADMIN_PANEL_SCREEN_CODES } from 'app/core/auth/admin-panel-permissions';
import { PermissionService } from 'app/core/services/permission.service';

@Component({
    selector: 'user',
    templateUrl: './user.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'user',
    standalone: true,
    imports: [
        MatButtonModule,
        MatMenuModule,
        NgIf,
        MatIconModule,
        NgClass,
        MatDividerModule,
        RouterModule,
        MatTooltipModule,
        AutoTranslateDirective,
        AutoTranslatePipe,
    ],
})
export class UserComponent implements OnInit, OnDestroy {
    /* eslint-disable @typescript-eslint/naming-convention */
    static ngAcceptInputType_showAvatar: BooleanInput;
    /* eslint-enable @typescript-eslint/naming-convention */

    @Input() showAvatar: boolean = true;

    @Input() isHome: boolean = false;
    user: UserModel;

    private _unsubscribeAll: Subject<any> = new Subject<any>();
    schema: string = 'auto';
    isDark: boolean = false;
    isAuto: boolean = false;
    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _router: Router,
        private _settingsService: SettingsService,
        private _devstroupeConfigService: DevstroupeConfigService,
        private dialog: MatDialog,
        private companyService: CompanyService,
        private permissionService: PermissionService,
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to user changes
        ApiService.userCurrent.pipe(takeUntil(this._unsubscribeAll)).subscribe((user: UserModel) => {
            this.user = user;

            // Mark for check
            this._changeDetectorRef.markForCheck();
        });
        this.permissionService.hasPermission$('COMPANY_COCKPIT_VIEW').pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
            this._changeDetectorRef.markForCheck();
        });
        this._devstroupeConfigService.config$.pipe(takeUntil(this._unsubscribeAll)).subscribe((config) => {
            this.schema = config.scheme;
            this._changeDetectorRef.markForCheck();
            this.isDark = config.scheme == 'dark' || (config.scheme == 'auto' && window.matchMedia('(prefers-color-scheme: dark)').matches);
            this.isAuto = config.scheme == 'auto';
        });
        this._router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntil(this._unsubscribeAll),
            )
            .subscribe(() => this._changeDetectorRef.markForCheck());

        this.companyService.currentCompany$.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
            this._changeDetectorRef.markForCheck();
        });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Update the user status
     *
     * @param status
     */
    updateUserStatus(status: string): void {
        // Return if user is not available
        if (!this.user) {
            return;
        }

        // Update the user
        // this._userService.update({
        //     ...this.user,
        //     // status,
        // }).subscribe();
    }

    /**
     * Sign out
     */
    signOut(): void {
        this._router.navigate(['/sign-out']);
    }

    toggleSettings() {
        this._settingsService.toggle();
    }

    toggleSchema(): void {
        const currentScheme = this.schema;
        let newScheme: string;

        if (currentScheme === 'auto') {
            newScheme = 'light';
        } else if (currentScheme === 'light') {
            newScheme = 'dark';
        } else {
            newScheme = 'auto';
        }

        this._devstroupeConfigService.config = { scheme: newScheme };
    }

    setTheme(scheme: string): void {
        this._devstroupeConfigService.config = { scheme };
    }

    setThemeAuto() {
        if (this.isAuto && window.matchMedia('(prefers-color-scheme: dark)').matches) {
            this.setTheme('dark');
        } else {
            this.setTheme('auto');
        }
    }

    get initials(): string {
        const words = (this.user?.name || '').trim().split(/\s+/).filter(Boolean);
        if (!words.length) return '?';
        if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
        return (words[0][0] + words[words.length - 1][0]).toUpperCase();
    }

    get permissionGroupLabel(): string {
        return this.user?.permission_group?.name?.trim() ?? '';
    }

    get isAdminRoute(): boolean {
        return this._router.url.split('?')[0].startsWith('/admin');
    }

    get isAllocationEngineRoute(): boolean {
        return this._router.url.split('?')[0].startsWith('/allocation-engine');
    }

    get isRequesterPortalActive(): boolean {
        return isRequesterPortalContext(this.user, this.companyService.getCurrentCompany(), this._router.url);
    }

    get showAllocationEngineMenu(): boolean {
        if (this.isAllocationEngineRoute) {
            return false;
        }
        // Mesma regra da rota /allocation-engine e da API (COMPANY_COCKPIT_VIEW + ENFORCE).
        return this.permissionService.hasPermission('COMPANY_COCKPIT_VIEW');
    }

    /** Exibe atalho ao painel só se houver ao menos uma tela ADMIN_* liberada. */
    get showAdminPanelMenu(): boolean {
        return !!this.user?.is_admin && this.permissionService.hasAnyPermission([...ADMIN_PANEL_SCREEN_CODES]);
    }

    openRequesterPortal(): void {
        this.companyService.rememberOperationalCompanyIfNeeded();
        void this._router.navigate([REQUESTER_HOME_PATH]);
    }

    returnToMainApp(): void {
        this.companyService
            .restoreOperationalCompany()
            .pipe(take(1))
            .subscribe(() => {
                void this._router.navigate(['/home']);
            });
    }

    editUser(): void {
        const config = {
            type: this.user.profile_type,
            isProfileEdit: true,
        };
        const dialogRef = this.dialog.open(UserFormComponent, {
            data: { ...this.user, config },
            width: '600px',
        });

        dialogRef.afterClosed().pipe(takeUntil(this._unsubscribeAll)).subscribe();
    }
}
